import { Injectable } from '@angular/core';
import { PatientProfileForms } from '@bepatient/patient-profile-store';
import { DynamicFormInputQuestion } from './dynamic-form.models';

@Injectable()
export class DynamicFormQuestionParserService {
  parseFilterForm(values: any): any {
    // we remove null, undefined, or empty string from the form value
    const onlyValidValues = this.objWithoutFaultyValue(values);

    // MOBI requires a specific date format like 2018/01/01 so we convert
    // ionic default date format
    const validValueWithParsedDate =
      this.convertDateStringsForMobi(onlyValidValues);

    return validValueWithParsedDate;
  }

  parseFormValuesForMobi(values, questions): any {
    return Object.keys(values).reduce((parsedValues, uuid) => {
      if (Array.isArray(values[uuid])) {
        const currentQuestion = questions.find(
          question => question.key === uuid
        );
        const arrayOfValues = values[uuid]
          .map((value, i) => {
            if (value) {
              return currentQuestion.nestedQuestions[i].key;
            }
          })
          .filter(value => value !== undefined);

        parsedValues[uuid] = arrayOfValues;
      } else {
        parsedValues[uuid] =
          values[uuid] === null ? '' : values[uuid].toString();
      }
      return parsedValues;
    }, {});
  }

  formatDataToPostToMobi(values: any, questions: any): any {
    const dateTimeQuestions = questions
      .filter(({ controlType }) => controlType === 'datetime')
      .filter(({ displayFormat }) => displayFormat === 'YYYY-MM-DD')
      .map(({ key }) => key);

    Object.keys(values).forEach(key => {
      if (dateTimeQuestions.includes(key) && !!values[key]) {
        const dateExpression = /^(\d{4})-(\d{2})-(\d{2})/gi;
        values[key] = values[key].match(dateExpression).pop();
      }
    });
  }

  public patientProfileParseQuestions(
    questions,
    responses?,
    formIsReadOnly?: boolean
  ): DynamicFormInputQuestion[] {
    const parsedQuestions = questions
      .filter(question => question.type !== 'richtext')
      .map(question => {
        const { field_key: key, label: title, type, logic } = question;
        const customClasses = !responses
          ? ['']
          : this.addCustomCSSClasses(question, responses);

        const isConditionnal =
          question &&
          question.logic &&
          question.logic.checks &&
          question.logic.checks.length > 0;

        const options = this.transformOptionsObjectToArray(question.options);
        return {
          key,
          title,
          logic,
          type: this.parseType(type),
          options,
          required: parseInt(question.required, 10) === 1,
          customClasses,
          readonly: formIsReadOnly,
          display: isConditionnal
            ? this.getQuestionDisplayValue(question)
            : true,
          conditionnalRules: isConditionnal
            ? this.addConditionnalRulesToQuestion(question)
            : undefined
        };
      });

    return parsedQuestions;
  }

  public patientProfileParseSelectedActivity(activity): any {
    return {
      date: activity.mtime,
      answersUuid: activity.uuid
    };
  }

  public patientProfileFormsParser(
    forms: Array<any>,
    isProForm: boolean
  ): PatientProfileForms[] {
    return forms.map(form => {
      const name = (form['meta'] && form['meta']['title']) || void 0;
      const date =
        (form['meta'] &&
          form['meta']['last_activity'] &&
          form['meta']['last_activity']['mtime']) ||
        void 0;

      const toBeChecked =
        (form['meta'] &&
          form['meta']['last_activity'] &&
          form['meta']['last_activity']['to_be_checked']) ||
        void 0;

      const uuid = form['uuid'] || void 0;
      const haveLastActivity =
        (form['meta'] && form['meta']['last_activity']) || void 0;

      const status =
        (form['meta'] &&
          form['meta']['last_activity'] &&
          form['meta']['last_activity']['status']) ||
        void 0;

      return {
        name,
        date,
        toBeChecked,
        uuid,
        haveLastActivity,
        isProForm,
        status
      };
    });
  }

  public patientProfileParseResponses(questions, responses): any {
    return responses.reduce((acc, response) => {
      const { uuid, value, to_be_checked: toBeChecked } = response;
      const currentQuestion = questions.find(
        question => question.field_key === uuid
      );
      const { type } = currentQuestion;
      switch (type) {
        case 'checkbox':
        case 'toggle':
          const options = this.transformOptionsObjectToArray(
            currentQuestion.options
          );
          acc[uuid] = options.map(question => value.includes(question.name));
          break;

        default:
          acc[uuid] = value;
          break;
      }
      return acc;
    }, {});
  }

  public isFormReadonly(links: any): boolean {
    return Object.keys(links).includes('submit') ? false : true;
  }

  private convertDateStringsForMobi(obj: any): any {
    return Object.keys(obj).reduce((acc, current) => {
      const value = obj[current];
      const formatDateString = str => {
        const dateExpression = /^(\d{4})-(\d{2})-(\d{2})$/g;
        return str.replace(dateExpression, '$3/$2/$1');
      };
      if (typeof value === 'string') {
        acc[current] = formatDateString(value);
      } else if (Array.isArray(value)) {
        acc[current] = value.map(_v => {
          if (typeof _v === 'string') {
            return formatDateString(_v);
          }
          return _v;
        });
      } else {
        acc[current] = value;
      }

      return acc;
    }, {});
  }

  // Returns a object with on valid value
  // they must be !== null or undefined or empty string or an array of those
  // elements
  private objWithoutFaultyValue(obj: any): any {
    const faultyValues = [null, undefined, ''];
    const isArray = arr => Array.isArray(arr),
      isOnlyNull = arr => arr.every(val => faultyValues.includes(val)),
      isObject = _obj => typeof _obj === 'object';

    return Object.keys(obj)
      .filter(key => !faultyValues.includes(obj[key]))
      .reduce((acc, key) => {
        if (isArray(obj[key])) {
          if (!isOnlyNull(obj[key])) {
            acc[key] = obj[key];
          }
        } else if (isObject(obj[key])) {
          if (!isOnlyNull(Object.values(obj[key]))) {
            acc[key] = Object.values(obj[key]);
          }
        } else {
          acc[key] = obj[key];
        }
        return acc;
      }, {});
  }

  private getQuestionDisplayValue({ logic }): boolean {
    return logic.action !== 'show';
  }

  private addConditionnalRulesToQuestion(question) {
    return question.logic.checks.reduce((prev, current) => {
      prev[current.field] = current.option;
      return prev;
    }, {});
  }

  private transformOptionsObjectToArray(optionsObject: any): any[] {
    return Object.keys(optionsObject)
      .map(option => optionsObject[option])
      .map(option => {
        const { value: name, label: value } = option;
        return { value, name };
      });
  }

  private addCustomCSSClasses(question, responses: any[]): string[] {
    const toBeCheckedClassname = 'dynamic-form__to-be-checked';
    const requiredClassname = 'dynamic-form__required';
    const classes = [];

    const response = responses.find(r => r.uuid === question['field_key']);

    if (response && (response.to_be_checked || response.toBeChecked)) {
      classes.push(toBeCheckedClassname);
    }

    if (parseInt(question.required, 10) === 1) {
      classes.push(requiredClassname);
    }

    return classes;
  }

  private parseType(type: string) {
    switch (type.toLowerCase()) {
      case 'richtext':
        return 'text';

      case 'integer':
        return 'number';

      case 'datetime':
        return 'date';

      case 'boolean':
        return 'radio';

      default:
        return type;
    }
  }
}
